/**
 * HTTP Client — Gateway-only
 * Simplified from axflow-frontend: no Direct mode, no token refresh
 */
import type { IHttpClient, RequestConfig } from '@domain/interfaces'
import { authActions } from '@store/authStore'
import { ENV } from '@shared/config/env'
import { createLoggerWithPrefix } from '@shared/utils/logger'
import { HttpError } from './errors'

const logger = createLoggerWithPrefix('httpClient')

export class HttpClient implements IHttpClient {
  private buildUrl(url: string, params?: RequestConfig['params']): string {
    const baseUrl = (ENV.GATEWAY_URL || window.location.origin).replace(/\/$/, '')
    const fullUrl = new URL(url, baseUrl)

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        fullUrl.searchParams.append(key, String(value))
      })
    }

    return fullUrl.toString()
  }

  private getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {}
    const sessionId = authActions.getToken()
    if (sessionId) {
      headers['X-Session-Id'] = sessionId
    }
    return headers
  }

  private async request<T>(
    method: string,
    url: string,
    data?: unknown,
    config?: RequestConfig
  ): Promise<T> {
    const fullUrl = this.buildUrl(url, config?.params)
    logger.debug('Request:', { method, url: fullUrl })

    const options: RequestInit = {
      method,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders(),
        ...config?.headers,
      },
    }

    if (data && method !== 'GET') {
      options.body = JSON.stringify(data)
    }

    const response = await fetch(fullUrl, options)

    // Handle 401 — session expired
    if (response.status === 401 && !config?._isRetry) {
      authActions.logout()
      throw new Error('Session expired. Please log in again.')
    }

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`
      let errorBody: unknown
      try {
        const errorText = await response.text()
        try {
          errorBody = JSON.parse(errorText)
          const errorData = errorBody as { message?: string; errors?: unknown[] }
          if (errorData.message) {
            errorMessage = errorData.message
          } else if (errorData.errors?.length) {
            errorMessage = String(errorData.errors[0])
          }
        } catch {
          errorMessage = errorText || errorMessage
        }
      } catch {
        // ignore
      }
      throw new HttpError(response.status, errorMessage, errorBody)
    }

    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return undefined as T
    }

    return response.json() as Promise<T>
  }

  async get<T>(url: string, config?: RequestConfig): Promise<T> {
    return this.request<T>('GET', url, undefined, config)
  }

  async post<T>(url: string, data?: unknown, config?: RequestConfig): Promise<T> {
    return this.request<T>('POST', url, data, config)
  }

  async put<T>(url: string, data?: unknown, config?: RequestConfig): Promise<T> {
    return this.request<T>('PUT', url, data, config)
  }

  async patch<T>(url: string, data?: unknown, config?: RequestConfig): Promise<T> {
    return this.request<T>('PATCH', url, data, config)
  }

  async delete<T>(url: string, config?: RequestConfig): Promise<T> {
    return this.request<T>('DELETE', url, undefined, config)
  }
}

export const httpClient = new HttpClient()
